/**
 * WhyBuyersLeverageSection
 *
 * Objection-handling homepage section addressing the question:
 * "Why wouldn't I just upload my inspection into ChatGPT?"
 *
 * Sits directly above the Common Questions FAQ. Tone is confident
 * and educational — NOT anti-AI, NOT competitor-bashing. Buyer-first.
 *
 * Telemetry (consent-gated via lib/analytics):
 *   - why_bl_section_viewed (fires once when section header enters viewport)
 *   - why_bl_comparison_viewed (fires once when chatbot-vs-BL grid is visible)
 *   - why_bl_cta_clicked { cta: "upload" | "sample" }
 *
 * Content is static (no API). Sub-sections, top to bottom:
 *   1. Section header + intro
 *   2. Hero callout (highlighted box)
 *   3. Side-by-side comparison (Chatbot vs Buyer's Leverage)
 *   4. "Designed Around The Questions Buyers Actually Ask" + cards grid
 *   5. Process diagram (Inspection Report → Engine → 5 outputs)
 *   6. "More Than A Summary" supporting copy
 *   7. CTA buttons
 */
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { track } from "@/lib/analytics";
import {
    ArrowDown,
    ArrowUpRight,
    Check,
    MessageSquareText,
    Target,
    FileText,
    ListChecks,
    DollarSign,
    Scale,
    UserCheck,
    Map,
    Cpu,
    BookOpen,
    HelpCircle,
} from "lucide-react";

/* ---------- Telemetry helpers ---------- */

// Fires `eventName` exactly once when the returned ref's element first
// scrolls into view. SSR-safe (IntersectionObserver guarded).
function useFireOnceWhenVisible(eventName, props) {
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current) return undefined;
        if (typeof window === "undefined" || !window.IntersectionObserver) {
            // Older browsers: fire immediately so analytics aren't lost.
            track(eventName, props);
            return undefined;
        }
        let fired = false;
        const node = ref.current;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting && !fired) {
                        fired = true;
                        track(eventName, props);
                        observer.disconnect();
                    }
                }
            },
            { threshold: 0.25 },
        );
        observer.observe(node);
        return () => observer.disconnect();
        // We intentionally don't depend on `props` — view events are one-shot.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventName]);
    return ref;
}

/* ---------- Section-1 hero callout ---------- */

function HeroCallout() {
    return (
        <div
            className="bl-card p-6 sm:p-8 md:p-10 relative overflow-hidden bg-bl-teal-tint/40 border-bl-primary/20"
            data-testid="why-bl-hero-callout"
            data-reveal="rise"
        >
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-bl-primary font-medium">
                <Cpu className="w-3.5 h-3.5" />
                Purpose-built · Not a general chatbot
            </div>
            <h3 className="mt-3 font-display font-semibold text-2xl sm:text-3xl md:text-[2.25rem] text-bl leading-snug tracking-tight max-w-3xl">
                A typical AI chatbot uses{" "}
                <span className="font-italic-accent text-bl-primary">
                    one conversation.
                </span>
                <br className="hidden sm:block" />{" "}
                Buyer's Leverage uses{" "}
                <span className="font-italic-accent text-bl-primary">
                    hundreds of specialized review processes.
                </span>
            </h3>
            <p className="mt-5 text-bl-muted leading-relaxed max-w-2xl text-[15px] sm:text-base">
                Instead of relying on a single prompt and conversation,
                Buyer's Leverage analyzes your inspection report through
                hundreds of purpose-built review processes designed
                specifically for home inspections, repair planning,
                negotiation preparation, and home-buying decisions.
            </p>
            <p className="mt-3 text-bl-muted leading-relaxed max-w-2xl text-[15px] sm:text-base">
                The result is a{" "}
                <span className="text-bl font-medium">structured report</span>{" "}
                designed to help buyers understand what matters, what may
                require follow-up, and what actions to consider next.
            </p>
        </div>
    );
}

/* ---------- Section-2 comparison ---------- */

const CHATBOT_TRAITS = [
    "Can summarize a report",
    "Can explain terminology",
    "Can answer follow-up questions",
    "General-purpose responses",
    "Requires users to know what questions to ask",
    "Output quality varies based on prompting",
    "Not specifically designed for home inspections",
];

const BL_TRAITS = [
    "Purpose-built for home inspections",
    "Automatically categorizes findings",
    "Prioritizes issues by category",
    "Identifies potential specialist follow-up needs",
    "Provides repair exposure planning ranges",
    "Generates buyer-friendly action plans",
    "Highlights negotiation-related considerations",
    "Creates structured reports you can save",
    "Includes tools, calculators, and educational resources",
    "Designed specifically for homebuyers and investors",
];

function ComparisonColumn({ title, eyebrow, icon: Icon, traits, accent }) {
    const isPrimary = accent === "primary";
    return (
        <div
            className={
                "bl-card p-6 sm:p-7 flex flex-col h-full " +
                (isPrimary
                    ? "border-bl-primary ring-1 ring-bl-primary/15"
                    : "bg-bl-bg/40")
            }
            data-testid={`why-bl-comparison-${isPrimary ? "bl" : "chatbot"}`}
        >
            <div
                className={
                    "inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-medium " +
                    (isPrimary ? "text-bl-primary" : "text-bl-muted")
                }
            >
                <Icon className="w-3.5 h-3.5" />
                {eyebrow}
            </div>
            <h3 className="mt-2.5 font-display font-semibold text-xl sm:text-2xl text-bl">
                {title}
            </h3>
            <ul className="mt-5 space-y-2.5 flex-1">
                {traits.map((t) => (
                    <li key={t} className="flex gap-2.5 text-[15px] text-bl">
                        <Check
                            className={
                                "w-4 h-4 mt-1 flex-shrink-0 " +
                                (isPrimary ? "text-bl-primary" : "text-bl-muted")
                            }
                        />
                        <span className="leading-relaxed">{t}</span>
                    </li>
                ))}
            </ul>
            {isPrimary && (
                <div className="mt-5 pt-5 border-t border-bl">
                    <div className="text-[11px] tracking-[0.14em] uppercase text-bl-muted">
                        The takeaway
                    </div>
                    <p className="mt-1.5 text-sm text-bl leading-relaxed">
                        Both can summarize. Only one is{" "}
                        <span className="font-italic-accent text-bl-primary">
                            structured around the decisions a homebuyer
                            actually faces.
                        </span>
                    </p>
                </div>
            )}
        </div>
    );
}

/* ---------- Section-3 buyer-questions grid ---------- */

const BUYER_QUESTIONS = [
    { q: "What should I worry about first?", icon: ListChecks },
    { q: "What issues may require specialist review?", icon: UserCheck },
    { q: "What findings are likely routine maintenance?", icon: BookOpen },
    { q: "What could be expensive?", icon: DollarSign },
    { q: "What should I discuss with my agent?", icon: MessageSquareText },
    { q: "What might be worth negotiating?", icon: Scale },
    { q: "What should I plan for after closing?", icon: Map },
];

function BuyerQuestionsGrid() {
    return (
        <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
            data-testid="why-bl-question-grid"
        >
            {BUYER_QUESTIONS.map((b, i) => {
                const Ico = b.icon;
                return (
                    <div
                        key={b.q}
                        className="bl-card bl-card-hover p-5 sm:p-6 flex items-start gap-3"
                        data-testid={`why-bl-question-card-${i}`}
                        data-reveal="rise-sm"
                        data-reveal-delay={Math.min(5, (i % 5) + 1)}
                    >
                        <div className="w-9 h-9 rounded-lg bg-bl-teal-tint text-bl-primary flex items-center justify-center shrink-0">
                            <Ico className="w-4 h-4" />
                        </div>
                        <div className="font-display font-medium text-bl text-[15px] sm:text-base leading-snug">
                            {b.q}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/* ---------- Section-4 process diagram ---------- */

const PROCESS_OUTPUTS = [
    { label: "Prioritized issues", icon: ListChecks },
    { label: "Repair exposure planning", icon: DollarSign },
    { label: "Negotiation insights", icon: Scale },
    { label: "Specialist recommendations", icon: UserCheck },
    { label: "Action plan", icon: Map },
];

function ProcessDiagram() {
    return (
        <div
            className="bl-card p-6 sm:p-8 md:p-10"
            data-testid="why-bl-process-diagram"
            data-reveal="rise"
        >
            <div className="bl-eyebrow text-center">The flow</div>
            <h3 className="mt-3 text-center font-display font-semibold text-xl sm:text-2xl text-bl">
                From inspection PDF to{" "}
                <span className="font-italic-accent text-bl-primary">
                    structured decision support.
                </span>
            </h3>

            <div className="mt-8 flex flex-col items-center gap-2">
                <div
                    className="bl-card px-5 py-3.5 inline-flex items-center gap-2.5 bg-bl-surface min-w-[220px] justify-center"
                    data-testid="why-bl-process-input"
                >
                    <FileText className="w-4 h-4 text-bl-primary" />
                    <span className="font-display font-medium text-bl">
                        Inspection Report
                    </span>
                </div>
                <ArrowDown className="w-5 h-5 text-bl-muted" aria-hidden />
                <div
                    className="px-5 py-3.5 inline-flex items-center gap-2.5 rounded-xl bg-bl-primary text-bl-surface min-w-[260px] justify-center shadow-sm"
                    data-testid="why-bl-process-engine"
                >
                    <Cpu className="w-4 h-4" />
                    <span className="font-display font-semibold">
                        Buyer's Leverage Analysis Engine
                    </span>
                </div>
                <ArrowDown className="w-5 h-5 text-bl-muted mt-1" aria-hidden />
            </div>

            <div
                className="mt-2 grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4"
                data-testid="why-bl-process-outputs"
            >
                {PROCESS_OUTPUTS.map((o, i) => {
                    const Ico = o.icon;
                    return (
                        <div
                            key={o.label}
                            className="bl-card p-4 flex flex-col items-center text-center gap-2 bg-bl-teal-tint/30 border-bl-primary/15"
                            data-testid={`why-bl-process-output-${i}`}
                        >
                            <div className="w-9 h-9 rounded-lg bg-bl-surface text-bl-primary flex items-center justify-center">
                                <Ico className="w-4 h-4" />
                            </div>
                            <div className="text-[13px] sm:text-sm text-bl font-medium leading-snug">
                                {o.label}
                            </div>
                        </div>
                    );
                })}
            </div>

            <p className="mt-7 text-xs text-bl-muted text-center max-w-2xl mx-auto leading-relaxed">
                Buyer's Leverage organizes inspection findings into a
                structured view. It is not a replacement for inspectors,
                contractors, engineers, agents, attorneys, or other
                professionals.
            </p>
        </div>
    );
}

/* ---------- Top-level section ---------- */

export default function WhyBuyersLeverageSection() {
    const sectionRef = useFireOnceWhenVisible("why_bl_section_viewed", {
        surface: "home",
    });
    const comparisonRef = useFireOnceWhenVisible("why_bl_comparison_viewed", {
        surface: "home",
    });
    const handleCtaClick = (cta) => {
        track("why_bl_cta_clicked", { cta, surface: "home" });
    };
    return (
        <section
            ref={sectionRef}
            className="bl-section bg-bl-surface border-y border-bl"
            data-testid="home-why-bl"
            data-reveal
        >
            <div className="bl-container">
                {/* Section header */}
                <div className="max-w-3xl">
                    <div className="bl-eyebrow">
                        <HelpCircle className="w-3.5 h-3.5" />
                        Why Buyer's Leverage
                    </div>
                    <h2 className="mt-4 font-display font-semibold text-3xl md:text-4xl lg:text-5xl tracking-tight text-bl leading-[1.1]">
                        Built for home inspections.{" "}
                        <span className="font-italic-accent text-bl-primary">
                            Not general conversations.
                        </span>
                    </h2>
                    <p className="mt-5 text-bl-muted text-base sm:text-lg max-w-2xl leading-relaxed">
                        You could paste your inspection PDF into any general AI
                        chatbot — and it would summarize it. Here's why
                        homebuyers and investors still choose a purpose-built
                        platform.
                    </p>
                </div>

                {/* Hero callout */}
                <div className="mt-9">
                    <HeroCallout />
                </div>

                {/* Comparison */}
                <div className="mt-12 md:mt-16">
                    <h3 className="font-display font-semibold text-2xl md:text-3xl text-bl tracking-tight">
                        Side-by-side at a glance.
                    </h3>
                    <p className="mt-2 text-bl-muted max-w-2xl text-sm sm:text-base">
                        Where general AI chatbots are versatile generalists,
                        Buyer's Leverage is a specialist tuned for one job.
                    </p>
                    <div
                        ref={comparisonRef}
                        className="mt-7 grid md:grid-cols-2 gap-5 md:gap-6 items-stretch"
                        data-testid="why-bl-comparison-grid"
                    >
                        <ComparisonColumn
                            title="General AI Chatbot"
                            eyebrow="Versatile generalist"
                            icon={MessageSquareText}
                            traits={CHATBOT_TRAITS}
                            accent="neutral"
                        />
                        <ComparisonColumn
                            title="Buyer's Leverage"
                            eyebrow="Purpose-built specialist"
                            icon={Target}
                            traits={BL_TRAITS}
                            accent="primary"
                        />
                    </div>
                </div>

                {/* Buyer-questions grid */}
                <div className="mt-12 md:mt-16">
                    <div className="max-w-3xl">
                        <h3 className="font-display font-semibold text-2xl md:text-3xl text-bl tracking-tight">
                            Designed around the questions{" "}
                            <span className="font-italic-accent text-bl-primary">
                                buyers actually ask.
                            </span>
                        </h3>
                        <p className="mt-3 text-bl-muted text-sm sm:text-base leading-relaxed">
                            Most buyers don't want a raw summary. They want
                            help understanding what deserves attention, what
                            may require follow-up, and what questions to ask
                            next. Buyer's Leverage organizes inspection
                            findings around practical decision-making —
                            rather than simply repeating what's already in
                            the report.
                        </p>
                    </div>
                    <div className="mt-7">
                        <BuyerQuestionsGrid />
                    </div>
                </div>

                {/* Process diagram */}
                <div className="mt-12 md:mt-16">
                    <ProcessDiagram />
                </div>

                {/* More than a summary */}
                <div className="mt-12 md:mt-16 max-w-3xl">
                    <h3 className="font-display font-semibold text-2xl md:text-3xl text-bl tracking-tight">
                        More than a summary.
                    </h3>
                    <p className="mt-3 text-bl-muted text-sm sm:text-base leading-relaxed">
                        Most AI tools stop at summarizing. Buyer's Leverage
                        transforms inspection findings into a structured
                        decision-support report designed to help buyers move
                        forward with greater clarity and confidence.
                    </p>
                    <p className="mt-3 text-bl-muted text-sm sm:text-base leading-relaxed">
                        The goal is not to replace inspectors, contractors,
                        engineers, agents, attorneys, or other professionals.
                        The goal is to help buyers better understand their
                        inspection report and prepare for next steps.
                    </p>
                </div>

                {/* CTA */}
                <div
                    className="mt-10 flex flex-wrap gap-3"
                    data-testid="why-bl-cta-row"
                >
                    <Link
                        to="/upload"
                        onClick={() => handleCtaClick("upload")}
                        className="bl-btn-primary"
                        data-testid="why-bl-cta-upload"
                    >
                        Upload Your Inspection Report
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <Link
                        to="/sample-analysis"
                        onClick={() => handleCtaClick("sample")}
                        className="bl-btn-secondary"
                        data-testid="why-bl-cta-sample"
                    >
                        View Sample Report
                    </Link>
                </div>
            </div>
        </section>
    );
}
